import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import { C, FONT, Icon, Plain } from './ui';

// "Toast Notification/Web" from ui-prototype preferred-worker-pool
// (client-detail.html): top right, white, a title and an optional grey line,
// closes by itself after 3.2 s. The app-wide Toast is a single-line snackbar,
// so the Lifecycle page has its own.
type ToastValue = { title: string; sub?: string };

type ToastContextValue = { toast: (title: string, sub?: string) => void };

const ToastContext = createContext<ToastContextValue>({
  toast: () => undefined,
});

export const useLifecycleToast = () => useContext(ToastContext);

export const TOAST_MS = 3200;

export const LifecycleToastProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [current, setCurrent] = useState<ToastValue | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>();

  const hide = useCallback(() => {
    clearTimeout(timer.current);
    setCurrent(null);
  }, []);

  const toast = useCallback((title: string, sub?: string) => {
    clearTimeout(timer.current);
    setCurrent({ title, sub });
    timer.current = setTimeout(() => setCurrent(null), TOAST_MS);
  }, []);

  useEffect(() => () => clearTimeout(timer.current), []);

  const value = useMemo(() => ({ toast }), [toast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {current &&
        createPortal(
          <div
            data-testid="lifecycleToast"
            role="status"
            style={{
              position: 'fixed',
              top: 24,
              right: 24,
              zIndex: 10001,
              maxWidth: 420,
              background: C.white,
              border: `1px solid ${C.stroke}`,
              borderRadius: 8,
              boxShadow: '0 0 3px rgba(0,0,0,0.16)',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '8px 20px',
              boxSizing: 'border-box',
              fontFamily: FONT,
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                paddingRight: 24,
                minWidth: 0,
              }}
            >
              <span
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  lineHeight: 1.4,
                  color: C.black,
                }}
              >
                {current.title}
              </span>
              {current.sub && (
                <span
                  style={{
                    fontSize: 14,
                    fontWeight: 400,
                    lineHeight: 1.4,
                    color: C.grey,
                  }}
                >
                  {current.sub}
                </span>
              )}
            </div>
            <Plain
              aria-label="Close"
              style={{ width: 24, height: 24, color: C.black }}
              title="Close"
              type="button"
              onClick={hide}
            >
              <Icon d="M18 6L6 18M6 6l12 12" />
            </Plain>
          </div>,
          document.body,
        )}
    </ToastContext.Provider>
  );
};
